import { FC, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { EditIcon, Loader2, MessageSquareIcon } from "lucide-react";

import { PromptLibraryItem, PromptLibraryItemSource, PromptLibraryTab } from "../promptLibrary.types";
import { useGetPromptLibraryItemsQuery } from "../promptLibrary.api";
import { usePromptLibraryContext } from "../promptLibrary.context";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toDefaultDateFormat } from "@/helpers/dates";
import { customRenderers } from "@/components/feature/markdown/customRenderers";
import { ModifyNewPromptLibraryItemDialog } from "@/modules/promptLibrary/components/modifyNewPromptLibraryItemDialog";
import { useGetUsersQuery } from "@/api/users-admin";
import MagnifyingGlassIcon from "@heroicons/react/20/solid/MagnifyingGlassIcon";
import { clsx } from "clsx";
import { createGetItemsPayload } from "@/modules/promptLibrary/promptLibrary.utils";
import { PromptLibraryRightDrawerTabs } from "@/modules/promptLibrary/components/promptLibraryRightDrawerTabs";

interface Props {
  source: PromptLibraryItemSource;
}

export const PromptLibraryRightDrawer: FC<Props> = ({ source }) => {
  const [activeTab, setActiveTab] = useState<PromptLibraryTab>("all");
  const [editingPromptItem, setEditingPromptItem] = useState<PromptLibraryItem | null>(null);
  const [searchPhrase, setSearchPhrase] = useState("");
  const [editablePromptId, setEditablePromptId] = useState<string | null>(null);
  const [editedPrompts, setEditedPrompts] = useState<Record<string, string>>({});
  const getItemsPayload = createGetItemsPayload(searchPhrase, activeTab);
  const { data, isLoading } = useGetPromptLibraryItemsQuery(getItemsPayload);
  const userIds = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.map(item => item.user_id);
  }, [data]);
  const userQuery = useGetUsersQuery(userIds);

  const { emit } = usePromptLibraryContext();

  const handleTabClick = (tab: PromptLibraryTab) => {
    setActiveTab(tab);
  };

  const handleEditClick = async ({ id }: PromptLibraryItem) => {
    if (editablePromptId === id) {
      setEditablePromptId(null);
    } else {
      setEditablePromptId(id);
    }
  };

  const setEditedImprovedPrompt = (id: string, value: string) => {
    setEditedPrompts(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-white">
      <PromptLibraryRightDrawerTabs onTabClick={handleTabClick}>
        <div className="items-cente !ml-0 flex px-2 py-2">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search names or notes"
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchPhrase}
              onChange={e => setSearchPhrase(e.target.value)}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">
              <MagnifyingGlassIcon className="w-5 text-gray-500" />
            </div>
          </div>
        </div>
      </PromptLibraryRightDrawerTabs>

      <div className="!ml-0 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[20%] divide-y divide-gray-300 bg-gray-50/80">Name</TableHead>
              <TableHead className="w-[80%] divide-y divide-gray-300 bg-gray-50/80">Prompt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data!
              .filter(item => item.source === source)
              .map(item => {
                const user = userQuery.data?.find(({ id }) => id === item.user_id);
                const prompt = item.id in editedPrompts ? editedPrompts[item.id] : item.prompt;

                return (
                  <TableRow key={item.id}>
                    <TableCell className="align-baseline">
                      <div>
                        <div>{item.name}</div>
                        <div className="text-xs text-gray-500">
                          {user?.name} {user?.surname}
                        </div>
                        <div className="text-xs text-gray-500">{toDefaultDateFormat(item.created_at)}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {editablePromptId === item.id ? (
                          <div
                            contentEditable
                            onBlur={t => setEditedImprovedPrompt(item.id, t.currentTarget.innerHTML)}
                            className={clsx(
                              "relative mb-4 whitespace-pre-line rounded-lg border border-gray-200 bg-white p-5 text-sm shadow-md",
                              {
                                "border-2 border-gray-500": editablePromptId === item.id,
                              },
                            )}
                          >
                            {prompt}
                          </div>
                        ) : (
                          <ReactMarkdown components={customRenderers}>{prompt}</ReactMarkdown>
                        )}
                      </div>
                      <div>
                        Notes: <span className="text-gray-500">{item.notes}</span>
                      </div>
                      <div className="flex justify-end gap-1">
                        <button
                          className="mt-2 flex items-center self-end rounded-md bg-brand-grey-600 px-2 py-1 text-xs text-white shadow-sm transition-colors hover:bg-gray-500"
                          onClick={() => handleEditClick(item)}
                        >
                          <EditIcon className="mr-1" size={12} /> Edit
                        </button>
                        <button
                          className="mt-2 flex items-center self-end rounded-md bg-brand-grey-600 px-2 py-1 text-xs text-white shadow-sm transition-colors hover:bg-gray-500"
                          onClick={() => {
                            emit(prompt);
                          }}
                        >
                          <MessageSquareIcon className="mr-1" size={12} /> Send to chat
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>

      {editingPromptItem && (
        <ModifyNewPromptLibraryItemDialog
          isOpen={!!editingPromptItem}
          setIsOpen={() => setEditingPromptItem(null)}
          item={editingPromptItem}
          mode="edit"
        />
      )}
    </div>
  );
};
